import React, { useState, useEffect, useMemo } from 'react';
import { Chess } from 'chess.js';

// Both colors use the SAME solid-filled glyph set (the "black chess piece"
// Unicode codepoints render as solid silhouettes in virtually every font,
// while the "white chess piece" codepoints are hollow outlines by design —
// using them for white pieces made them look thin/incomplete). Color is
// applied purely via CSS fill, so white pieces now render as true solid
// white shapes, matching the black pieces' weight.
import { getBoardTheme, getPieceTheme } from '../../utils/chessThemes';

// \uFE0E (variation selector 15) forces TEXT rendering of the glyphs.
// Without it, many Android phones render these as EMOJI — ignoring our
// CSS colors, so both sides looked identically "white" to spectators.
const TXT = '\uFE0E';
const PIECE_GLYPHS = { p: '♟', r: '♜', n: '♞', b: '♝', q: '♛', k: '♚' };

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
const RANKS = ['8', '7', '6', '5', '4', '3', '2', '1'];

/**
 * ChessBoard — big, touch-friendly, legal-move-enforcing chess board.
 *
 * Props:
 *  - fen: current position (FEN string)
 *  - onMove({ fen, san, from, to, isGameOver, isCheckmate, isDraw, isStalemate, winner, turn }): called after a legal move
 *  - interactive: whether the board accepts input at all
 *  - playerColor: 'w' | 'b' | null — if set, only pieces of this color can be moved (online mode). If null, both sides movable (local/analysis mode).
 *  - orientation: 'white' | 'black' — which side is at the bottom
 *  - lastMove: { from, to } — highlights the last move played
 */
function ChessBoard({
  fen,
  onMove,
  interactive = true,
  playerColor = null,
  boardTheme = null,
  pieceTheme = null,
  orientation = 'white',
  lastMove = null,
}) {
  const [selected, setSelected] = useState(null);
  const [legalTargets, setLegalTargets] = useState([]);
  const [pendingPromotion, setPendingPromotion] = useState(null); // { from, to, color }

  const game = useMemo(() => {
    try {
      return new Chess(fen);
    } catch (e) {
      return new Chess();
    }
  }, [fen]);

  useEffect(() => {
    setSelected(null);
    setLegalTargets([]);
    setPendingPromotion(null);
  }, [fen]);

  // (piece lookups use game.get(square) directly now)

  function squareAt(fileIdx, rankIdx) {
    return `${FILES[fileIdx]}${RANKS[rankIdx]}`;
  }

  function pieceAt(square) {
    const p = game.get(square);
    if (!p) return null;
    return { type: p.type, color: p.color };
  }

  function handleSquareClick(square) {
    if (!interactive || pendingPromotion) return;

    const piece = game.get(square);

    // Selecting a piece
    if (!selected) {
      if (!piece) return;
      if (playerColor && piece.color !== playerColor) return;
      if (piece.color !== game.turn()) return; // not your turn
      setSelected(square);
      const moves = game.moves({ square, verbose: true });
      setLegalTargets(moves.map(m => m.to));
      return;
    }

    // Clicked same square again → deselect
    if (square === selected) {
      setSelected(null);
      setLegalTargets([]);
      return;
    }

    // Clicked another own piece → reselect
    if (piece && (!playerColor || piece.color === playerColor) && piece.color === game.turn()) {
      setSelected(square);
      const moves = game.moves({ square, verbose: true });
      setLegalTargets(moves.map(m => m.to));
      return;
    }

    // Attempt move
    if (legalTargets.includes(square)) {
      const movingPiece = game.get(selected);
      const isPromotion =
        movingPiece?.type === 'p' &&
        ((movingPiece.color === 'w' && square[1] === '8') ||
         (movingPiece.color === 'b' && square[1] === '1'));

      if (isPromotion) {
        setPendingPromotion({ from: selected, to: square, color: movingPiece.color });
        return;
      }
      commitMove(selected, square);
    } else {
      setSelected(null);
      setLegalTargets([]);
    }
  }

  function commitMove(from, to, promotion) {
    try {
      const moveResult = game.move({ from, to, promotion: promotion || 'q' });
      if (!moveResult) return;
      setSelected(null);
      setLegalTargets([]);
      setPendingPromotion(null);

      let winner = null;
      if (game.isCheckmate()) winner = moveResult.color === 'w' ? 'white' : 'black';
      else if (game.isDraw() || game.isStalemate()) winner = 'draw';

      onMove && onMove({
        fen: game.fen(),
        san: moveResult.san,
        from,
        to,
        isGameOver: game.isGameOver(),
        isCheckmate: game.isCheckmate(),
        isDraw: game.isDraw(),
        isStalemate: game.isStalemate(),
        isCheck: game.isCheck(),
        winner,
        turn: game.turn(),
      });
    } catch (e) {
      setSelected(null);
      setLegalTargets([]);
    }
  }

  const displayRanks = orientation === 'white' ? RANKS : [...RANKS].reverse();
  const displayFiles = orientation === 'white' ? FILES : [...FILES].reverse();

  const kingInCheckSquare = (() => {
    if (!game.isCheck()) return null;
    const turnColor = game.turn();
    for (let r = 0; r < 8; r++) {
      for (let f = 0; f < 8; f++) {
        const sq = squareAt(f, r);
        const p = game.get(sq);
        if (p && p.type === 'k' && p.color === turnColor) return sq;
      }
    }
    return null;
  })();

  const bt = boardTheme || getBoardTheme();
  const pt = pieceTheme || getPieceTheme();

  return (
    <div style={styles.wrap}>
      <style>{`
        @keyframes iyannaPieceHop {
          0%   { transform: translateY(0) scale(0.94); }
          38%  { transform: translateY(-26%) scale(1.14); }
          72%  { transform: translateY(4%) scale(0.98); }
          100% { transform: translateY(0) scale(1); }
        }
        @keyframes iyannaCheckPulse {
          0%, 100% { opacity: 0.85; transform: scale(0.94); }
          50%      { opacity: 1; transform: scale(1.06); }
        }
      `}</style>
      <div style={styles.board}>
        {displayRanks.map((rank, rIdx) => (
          <div key={rank} style={styles.row}>
            {displayFiles.map((file, fIdx) => {
              const square = `${file}${rank}`;
              const isLight = (FILES.indexOf(file) + RANKS.indexOf(rank)) % 2 === 0;
              const piece = pieceAt(square);
              const isSelected = selected === square;
              const isTarget = legalTargets.includes(square);
              const isLastMove = lastMove && (lastMove.from === square || lastMove.to === square);
              const isCheckSquare = kingInCheckSquare === square;
              const hasPieceOnTarget = isTarget && piece;

              const isHopSquare = lastMove && lastMove.to === square && piece;

              return (
                <div
                  key={square}
                  onClick={() => handleSquareClick(square)}
                  style={{
                    ...styles.square,
                    background: isLight ? bt.light : bt.dark,
                    ...(isSelected ? styles.selected : {}),
                    cursor: interactive ? 'pointer' : 'default',
                  }}
                >
                  {/* Check: a soft red glow behind the king only — not a
                      box around the whole square (that looked old-fashioned). */}
                  {isCheckSquare && <div style={styles.checkGlow} />}
                  {/* Last-move highlight as an overlay so the square color
                      still shows through (inline "!important" is invalid in
                      React and was silently breaking this highlight). */}
                  {isLastMove && <div style={styles.lastMoveOverlay} />}
                  {piece && (
                    <span
                      // Re-keying by fen forces a fresh DOM node on the
                      // destination square, so the HOP animation replays on
                      // every move — the piece visibly JUMPS to its square
                      // instead of appearing to slide.
                      key={isHopSquare ? `${square}-${fen}` : square}
                      style={{
                      ...styles.piece,
                      ...(isHopSquare ? styles.pieceHop : {}),
                      color: piece.color === 'w' ? pt.w : pt.b,
                      WebkitTextStroke: piece.color === 'w' ? `1.2px ${pt.wStroke}` : `1px ${pt.bStroke}`,
                      textShadow: piece.color === 'w'
                        ? '0 2px 3px rgba(0,0,0,0.5)'
                        : '0 1px 2px rgba(0,0,0,0.35)',
                    }}>
                      {PIECE_GLYPHS[piece.type] + TXT}
                    </span>
                  )}
                  {isTarget && !hasPieceOnTarget && <div style={styles.dot} />}
                  {isTarget && hasPieceOnTarget && <div style={styles.captureRing} />}
                  {fIdx === 0 && (
                    <span style={styles.rankLabel}>{rank}</span>
                  )}
                  {rIdx === 7 && (
                    <span style={styles.fileLabel}>{file}</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {pendingPromotion && (
        <div style={styles.promotionOverlay}>
          <div style={styles.promotionBox}>
            <div style={styles.promotionTitle}>Chagua kipande / Choose piece</div>
            <div style={styles.promotionRow}>
              {['q', 'r', 'b', 'n'].map(p => (
                <button
                  key={p}
                  style={styles.promotionBtn}
                  onClick={() => commitMove(pendingPromotion.from, pendingPromotion.to, p)}
                >
                  <span style={{
                    color: pendingPromotion.color === 'w' ? pt.w : pt.b,
                    WebkitTextStroke: pendingPromotion.color === 'w' ? '1px #2b2b2b' : '0.5px rgba(255,255,255,0.15)',
                  }}>
                    {PIECE_GLYPHS[p] + TXT}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function areEqual(prev, next) {
  return (
    prev.fen === next.fen &&
    prev.interactive === next.interactive &&
    prev.playerColor === next.playerColor &&
    prev.orientation === next.orientation &&
    prev.lastMove?.from === next.lastMove?.from &&
    prev.lastMove?.to === next.lastMove?.to &&
    prev.boardTheme?.id === next.boardTheme?.id &&
    prev.pieceTheme?.id === next.pieceTheme?.id
  );
}

export default React.memo(ChessBoard, areEqual);

const styles = {
  wrap: { position: 'relative', width: '100%', maxWidth: '560px', margin: '0 auto' },
  board: {
    width: '100%',
    aspectRatio: '1 / 1',
    border: '8px solid #5A3A1E',
    outline: '1px solid rgba(255,255,255,0.35)',
    outlineOffset: '-9px',
    borderRadius: '14px',
    boxShadow: '0 18px 44px rgba(20,40,28,0.3), 0 4px 10px rgba(20,40,28,0.15), inset 0 0 0 1px rgba(0,0,0,0.18)',
    overflow: 'hidden',
    background: '#5A3A1E',
  },
  row: { display: 'flex', width: '100%', height: '12.5%' },
  square: {
    position: 'relative',
    width: '12.5%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
  },
  piece: {
    position: 'relative',
    zIndex: 2,
    fontFamily: "'Segoe UI Symbol', 'Noto Sans Symbols 2', 'DejaVu Sans', sans-serif",
    fontSize: 'min(9vw, 44px)',
    lineHeight: 1,
    filter: 'drop-shadow(0 3px 3px rgba(0,0,0,0.4))',
  },
  pieceHop: {
    animation: 'iyannaPieceHop 0.32s cubic-bezier(0.3, 0.9, 0.4, 1.1)',
    willChange: 'transform',
  },
  selected: { boxShadow: 'inset 0 0 0 4px rgba(16,185,129,0.85)' },
  lastMoveOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(74,222,128,0.45)',
    pointerEvents: 'none',
  },
  check: { boxShadow: 'inset 0 0 0 4px rgba(211,47,47,0.9)' },
  checkGlow: {
    position: 'absolute', inset: '8%', borderRadius: '50%', zIndex: 1,
    background: 'radial-gradient(circle, rgba(239,68,68,0.9) 0%, rgba(239,68,68,0.55) 42%, rgba(239,68,68,0) 72%)',
    animation: 'iyannaCheckPulse 1.1s ease-in-out infinite',
    pointerEvents: 'none',
  },
  dot: {
    position: 'absolute',
    width: '28%',
    height: '28%',
    borderRadius: '50%',
    background: 'rgba(20,69,47,0.55)',
  },
  captureRing: {
    position: 'absolute',
    inset: '6%',
    borderRadius: '50%',
    boxShadow: 'inset 0 0 0 4px rgba(211,47,47,0.7)',
  },
  rankLabel: {
    position: 'absolute',
    top: '2px',
    left: '3px',
    fontSize: '10px',
    fontWeight: '700',
    color: 'rgba(0,0,0,0.35)',
  },
  fileLabel: {
    position: 'absolute',
    bottom: '2px',
    right: '3px',
    fontSize: '10px',
    fontWeight: '700',
    color: 'rgba(0,0,0,0.35)',
  },
  promotionOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    zIndex: 5,
  },
  promotionBox: {
    background: '#fff',
    borderRadius: '16px',
    padding: '18px',
    textAlign: 'center',
    boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
  },
  promotionTitle: { fontSize: '13px', fontWeight: '600', color: '#1C1C1E', marginBottom: '10px' },
  promotionRow: { display: 'flex', gap: '10px' },
  promotionBtn: {
    width: '54px',
    height: '54px',
    fontSize: '32px',
    borderRadius: '10px',
    border: '2px solid #E5E7EB',
    background: '#F9FAFB',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};
